import { Injectable } from '@angular/core';
import { of, Observable, throwError } from 'rxjs';
import { catchError, tap, share } from 'rxjs/operators';

import { Logger } from './logger';

interface CacheContent {
    expiry: number;
    value: any;
}

/**
 * Cache Service is an observables based in-memory cache implementation
 * Keeps track of in-flight observables and sets a default expiry for cached values
 * https://hackernoon.com/angular-simple-in-memory-cache-service-on-the-ui-with-rxjs-77f167387e39
 */
@Injectable({
    providedIn: 'root',
})
export class CacheService {
    private cache: Map<string, CacheContent> = new Map<string, CacheContent>();
    private inFlightObservables: Map<string, Observable<any>> = new Map<string, Observable<any>>();
    readonly DEFAULT_MAX_AGE: number = 300000;

    constructor(private logger: Logger) {}

    /**
     * Gets the value from cache if the key is provided.
     * If no value exists in cache, then check if the same call exists
     * in flight, if so return the fallback. If not create a new
     * fallback inFlightObservable and return the source observable.
     */
    get<T>(key: string, fallback?: Observable<T>, maxAge?: number): Observable<T> {
        if (this.hasValidCachedValue(key)) {
            this.logger.trace(`%cGetting from cache ${key}`, 'color: green');
            return of(this.cache.get(key).value);
        }

        if (!maxAge) {
            maxAge = this.DEFAULT_MAX_AGE;
        }

        if (this.inFlightObservables.has(key)) {
            return this.inFlightObservables.get(key);
        }

        if (!fallback || !(fallback instanceof Observable)) {
            return throwError(() => new Error('Requested key is not available in Cache'));
        }

        const obs = fallback.pipe(
            tap((value) => {
                this.set(key, value, maxAge);
            }),
            share(),
            catchError((e: any) => {
                this.inFlightObservables.delete(key);

                return throwError(() => e);
            }),
        );

        this.inFlightObservables.set(key, obs);

        this.logger.debug(`%c Calling api for ${key}`, 'color: purple');

        return obs;
    }

    /**
     * Sets the value with key in the cache
     * Notifies all observers of the new value
     */
    set<T>(key: string, value: T, maxAge: number = this.DEFAULT_MAX_AGE): void {
        this.cache.set(key, { value: value, expiry: Date.now() + maxAge });
    }

    /**
     * Checks if the a key exists in cache
     */
    has(key: string): boolean {
        return this.cache.has(key);
    }

    /**
     * Removes a key from the cache
     */
    delete(key: string): boolean;
    delete(key: RegExp): boolean;
    delete(keyorRegExp: string | RegExp): boolean {
        if (typeof keyorRegExp === 'string') {
            return this.cache.delete(keyorRegExp);
        }

        for (const key of this.cache.keys()) {
            if (keyorRegExp.test(key)) {
                this.cache.delete(key);
            }
        }
    }

    /**
     * Checks if the key exists and   has not expired.
     */
    private hasValidCachedValue(key: string): boolean {
        if (this.cache.has(key)) {
            if (this.cache.get(key).expiry < Date.now()) {
                this.cache.delete(key);
                return false;
            }
            return true;
        } else {
            return false;
        }
    }
}
